import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import NoticeModal from "../components/NoticeModal";
import NoticeFixModal from "../components/NoticeFixModal";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import { getPresetDateTimeRange } from "../utils/dateRange";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function Notice({ noticeType }) {
  const [users, setUsers] = useState([]);
  const [boards, setBoards] = useState([]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();

  const today = dayjs().tz("Asia/Seoul");
  const firstDayOfLastYear = today.subtract(1, "year").startOf("year");

  const [startDateTime, setStartDateTime] = useState(
    firstDayOfLastYear.format("YYYY-MM-DDTHH:mm"),
  );
  const [endDateTime, setEndDateTime] = useState(
    today.format("YYYY-MM-DDTHH:mm"),
  );
  const [searchKeyword, setSearchKeyword] = useState("");

  // 보기 모달
  const [showModal, setShowModal] = useState(false);

  // 수정/글쓰기 모달
  const [showFixModal, setShowFixModal] = useState(false);

  // 선택된 공지 데이터
  const [selectedNotice, setSelectedNotice] = useState(null);

  const filteredBoards = boards.filter((log) =>
    log.subject?.toLowerCase().includes(searchKeyword.toLowerCase()),
  );

  const handleDateRange = (type) => {
    const range = getPresetDateTimeRange(type);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  const openModal = (notice) => {
    setSelectedNotice(notice);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotice(null);
  };

  const fetchBoardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_BASE}/api/notice/notice`, null, {
        params: {
          board_type: noticeType,
          processed_at_start: startDateTime.replace("T", " ") + ":00",
          processed_at_end: endDateTime.replace("T", " ") + ":59",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBoards(res.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if ([401, 403, 500].includes(err.response.status)) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("공지사항 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/Users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if ([401, 403, 500].includes(err.response.status)) {
            localStorage.removeItem("token");
            setIsLoggedIn(false);
            navigate("/login");
            return;
          }
        }
        console.error("유저 정보 불러오기 실패:", err);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    fetchBoardData();
  }, [noticeType]);

  const renderRow = (log, idx) => {
    if (!boards || boards.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan="14">
            <h5>조회된 정보가 없습니다.</h5>
          </td>
        </tr>
      );
    }

    return (
      <tr
        key={log.idx}
        data-pos-y={idx}
        style={{ cursor: "pointer" }}
        onClick={() =>
          openModal({
            subject: log.subject,
            content: log.content, // DB의 필드명 따라 수정!
          })
        }
      >
        <td>
          <span className="text-primary">{log.subject}</span>
        </td>

        <td className="text-center">{log.view_count}</td>

        <td className="text-center">
          <div>
            <span className="title fw-bold">{log.reg_user_id}</span>
            <br />
            <span style={{ fontSize: "95%" }}>
              {dayjs(log.reg_dt).tz("Asia/Seoul").format("YYYY-MM-DD")} (
              {dayjs(log.reg_dt).tz("Asia/Seoul").format("HH:mm:ss")} UTC+9)
            </span>
          </div>
        </td>

        {users.myLevel === "0" && (
          <td className="text-center">
            <button
              className="btn trann"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedNotice({
                  subject: log.subject,
                  content: log.content,
                  idx: log.idx,
                });
                setShowModal(false);
                setShowFixModal(true);
              }}
            >
              수정
            </button>

            <button
              className="btn trann"
              onClick={async (e) => {
                e.stopPropagation();

                if (!window.confirm("정말 삭제하시겠습니까?")) return;

                const token = localStorage.getItem("token");
                await axios.delete(`${API_BASE}/api/notice/delete/${log.idx}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });

                fetchBoardData();
              }}
            >
              삭제
            </button>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vLogsAgent">
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3 p-0">
                    <form
                      className="mb-0"
                      onSubmit={(e) => {
                        e.preventDefault();
                        fetchBoardData();
                      }}
                    >
                      <DateTimeRangePicker
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        onStartChange={setStartDateTime}
                        onEndChange={setEndDateTime}
                        onPresetChange={handleDateRange}
                      >
                        <div className="col-12 col-sm-6 col-lg-6 col-xl-3 col-xxl-4">
                          <div className="form-control-wrap">
                            <div
                              className="input-group"
                              style={{ flexWrap: "nowrap" }}
                            >
                              <div className="btn btn-primary">제목</div>

                              <input
                                className="form-control"
                                placeholder="검색어"
                                value={searchKeyword}
                                onChange={(e) =>
                                  setSearchKeyword(e.target.value)
                                }
                              />

                              <button
                                type="submit"
                                className="btn btn-outline-primary"
                              >
                                검색
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => {
                                  setSelectedNotice(null);
                                  setShowFixModal(true);
                                }}
                              >
                                글쓰기
                              </button>
                            </div>
                          </div>
                        </div>
                      </DateTimeRangePicker>
                    </form>
                  </div>

                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                          <thead>
                            <tr>
                              <th className="text-center">제목</th>
                              <th className="text-center">조회수</th>
                              <th className="text-center">작성자</th>
                              {users.myLevel === "0" && (
                                <th className="text-center">옵션</th>
                              )}
                            </tr>
                          </thead>

                          <PaginatedSortableTable
                            data={filteredBoards}
                            renderRow={renderRow}
                            defaultSortField="reg_dt"
                            defaultSortOrder="desc"
                          />
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 보기 모달 */}
      <NoticeModal
        selectedNotice={selectedNotice}
        show={showModal}
        onClose={closeModal}
      />

      {/* 수정 / 글쓰기 모달 */}
      <NoticeFixModal
        show={showFixModal}
        initialData={selectedNotice}
        onSave={async (data) => {
          const token = localStorage.getItem("token");

          if (selectedNotice?.idx) {
            // 수정
            await axios.post(
              `${API_BASE}/api/notice/update/${selectedNotice.idx}`,
              {
                subject: data.subject,
                content: data.content,
              },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          } else {
            // 글쓰기
            await axios.post(
              `${API_BASE}/api/notice/create`,
              {
                subject: data.subject,
                content: data.content,
                type: noticeType, // 중요!
              },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          }

          // 저장 후 목록 갱신
          fetchBoardData();
        }}
        onClose={() => {
          setShowFixModal(false);
          setSelectedNotice(null);
        }}
      />
    </div>
  );
}
